import {PageFragment} from "@/shared/PageFragment";
import React, {ReactElement} from "react";
import {StyledBoxColumn, StyledBoxRow, StyledContainer, StyledListItem, StyledSpan} from "@/shared/StyledComponents";
import {Button, SvgIcon, Typography} from "@mui/material";
import {CheckCircle, CheckCircleOutlineOutlined, LocalHospitalOutlined, PersonOutlined} from "@mui/icons-material";
import {PAGE_FRAGMENT_HALF_WIDTH, PAGE_FRAGMENT_WIDTH, PD_LG, PD_XSM} from "@/shared/dimens";
import {BaseApp} from "@/shared/BaseApp";
import {QuickOnboardingStep} from "@/libwyze-shared/types";
import {OnboardingStatus, ProviderSettings} from "./settings";
import {colorGreen} from "@/shared/colors";
import {QuickOnboardingStepCompleted} from "./types";
import {SetupProviderPageProfileHelper} from "./SetupProviderPageProfileHelper";
import {SetupPracticePageProfileHelper} from "./SetupPracticePageProfileHelper";

type OnboardingStepButton = {
  step: QuickOnboardingStep,
  title: string,
  text: string,
  iconType: typeof SvgIcon,
  optional?: boolean,
  launch?: () => void,
}

const ONBOARDING_STEP_BUTTONS: OnboardingStepButton[] = [
  {
    step: QuickOnboardingStep.QUICK_SETUP_PROVIDER,
    title: "Setup your profile",
    text: "We'll create your public page for patients to communicate and book appointments.",
    iconType: PersonOutlined,
    launch: () => SetupProviderPageProfileHelper.setup(),
  },
  {
    step: QuickOnboardingStep.QUICK_SETUP_PRACTICE,
    title: "Setup or link a practice",
    text: "Setup or link a practice that you own or consult for.",
    iconType: LocalHospitalOutlined,
    launch: () => SetupPracticePageProfileHelper.setup(),
  },
];

export class ProviderQuickOnboardingFragment extends PageFragment {

  renderContent(): ReactElement {
    return <StyledContainer style={{flexGrow: 1, alignItems: "center", justifyContent: "center", gap: PD_LG}}>
      <Button
        onClick={() => BaseApp.CONTEXT.showDialog(null, () => <StyledBoxColumn
          style={{width: "92dvw", maxWidth: PAGE_FRAGMENT_HALF_WIDTH, padding: PD_LG}}>
          <Typography>Are you sure you want to skip onboarding? Some features within the app may become unavailable or
            not work.</Typography>
          <StyledBoxRow>
            <Button onClick={() => BaseApp.CONTEXT.hideDialog()}>Cancel</Button>
            <StyledSpan/>
            <Button onClick={() => this.setStatusAndReload(OnboardingStatus.SKIPPED)}>Skip</Button>
          </StyledBoxRow>
        </StyledBoxColumn>)}
        style={{position: "absolute", top: 12, right: 12}}>Skip</Button>
      <Typography variant="h4" style={{textAlign: "center"}}>Welcome to {BaseApp.CONTEXT.getAppConfig().name}</Typography>
      <Typography style={{textAlign: "center"}}>You're almost there! Complete the following steps to start using the
        app.</Typography>
      <StyledBoxColumn style={{width: "100%", maxWidth: PAGE_FRAGMENT_WIDTH}}>
        {ONBOARDING_STEP_BUTTONS.map((button, index) => {
          const lastCompleted = index === 0 || QuickOnboardingStepCompleted(ONBOARDING_STEP_BUTTONS[index - 1].step);
          const completed = QuickOnboardingStepCompleted(button.step);
          return <StyledListItem
            style={{opacity: (!lastCompleted || completed) ? 0.5 : 1}}
            disabled={!lastCompleted || completed}
            accessory={<div style={{flexShrink: 0}}>
              {completed
                ? <CheckCircle style={{color: colorGreen}}/>
                : <CheckCircleOutlineOutlined style={{opacity: 0.33}}/>}
            </div>}
            key={`step-${index}`}
            title={button.title}
            text={button.text}
            textWrap={true}
            icon={button.iconType}
            onClick={() => button.launch?.()}/>;
        })}
      </StyledBoxColumn>
      <Button
        onClick={() => this.setStatusAndReload(OnboardingStatus.COMPLETED)}
        disabled={Boolean(ONBOARDING_STEP_BUTTONS.find(button => !button.optional && !QuickOnboardingStepCompleted(button.step)))}
        variant="contained"
        style={{padding: PD_XSM, width: 300}}>
        Continue
      </Button>
    </StyledContainer>
  }

  private setStatusAndReload(status: OnboardingStatus): void {
    const onboarding = ProviderSettings.getInstance().onboarding;
    onboarding.status = status;
    BaseApp.CONTEXT.showProgressDialog("Please wait...", null, async function () {
      await onboarding.save();
      BaseApp.CONTEXT.hideDialog();
      window.location.reload();
    });
  }
}